import React, { FunctionComponent, useEffect, useState } from 'react';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import {
  PrimaryButton,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
// eslint-disable-next-line @trello/less-matches-component
import styles from './AutomaticOnboardingOverlay.less';
import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import api from 'app/gamma/src/api';
import { ProfileSyncResponse } from 'app/gamma/src/types/responses';
import { useAlert, AlertContainer } from 'app/gamma/src/components/alert';
import { AtlassianAccountOnboardingLinks } from '../../AtlassianAccountOnboardingLinks';
import { Login } from '../../AtlassianAccountOnboardingOverlayV2';
import { Avatars } from '@trello/members';
import { AutoOnboardingDialog } from '../../AutomaticOnboarding/AtlassianAutomaticOnboardingOverlays/AutoOnboardingDialog';

const ATLASSIAN_PROFILE_SYNC_ALERT_AREA = 'ATLASSIAN_PROFILE_SYNC_ALERT_AREA';
const ATLASSIAN_PROFILE_SYNC_ALERT_NAME = 'ATLASSIAN_PROFILE_SYNC_ALERT_NAME';

const format = forNamespace(['aa onboarding', 'dialog'], {
  shouldEscapeStrings: false,
});

const formatError = forTemplate('app_management');
/**
 * Extracted to allow testing/storybooking this component.
 * Not expecting this property to be overridden by consumers.
 */
const defaultProfileSyncResponseProvider = () => {
  return api.client.rest.post<ProfileSyncResponse>(
    'members/me/atlassianAccount/autoOnboarding',
    {
      query: {
        returnUrl: window.location.pathname,
      },
    },
  );
};
interface AtlassianProfileProps {
  avatars: Avatars;
  name: string;
  aaEmail: string;
  otherLogins: Login[];
  credentialsRemovedCount?: string | null;
  orgName?: string;
  onTrackScreenEvent: () => void;
  onDismiss: () => void;
  onClickOk: () => Promise<void>;
}

export const AtlassianProfile: FunctionComponent<AtlassianProfileProps> = ({
  avatars,
  name,
  aaEmail,
  otherLogins,
  orgName,
  onDismiss,
  onClickOk,
  onTrackScreenEvent,
  credentialsRemovedCount,
}) => {
  const showMultiEmailMsg = credentialsRemovedCount || otherLogins.length > 0;

  const { showAlert: showErrorAlert } = useAlert({
    alertArea: ATLASSIAN_PROFILE_SYNC_ALERT_AREA,
    alertName: ATLASSIAN_PROFILE_SYNC_ALERT_NAME,
  });

  const [requiresEmailVerification, setRequiresEmailVerification] = useState(
    false,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    onTrackScreenEvent();
  }, [onTrackScreenEvent]);

  const atlassianLogin = async () => {
    try {
      setIsLoading(true);
      const response = await defaultProfileSyncResponseProvider();
      await onClickOk();
      if (response.emailVerificationRequired) {
        setRequiresEmailVerification(true);
        setIsLoading(false);
      } else {
        window.location.assign(response.redirect_url);
      }
    } catch (err) {
      setIsLoading(false);
      if (err.error_code === 'ALREADY_MASTERED') {
        onDismiss();
        return;
      }
      showErrorAlert();
    }
  };

  if (requiresEmailVerification) {
    return (
      <AutoOnboardingDialog
        title={format('check your inbox')}
        helpLink={AtlassianAccountOnboardingLinks.HelpV2}
        closeDialog={onDismiss}
        className={styles.loginWithAtlassianBody}
      >
        <div className={styles.loginWithAtlassianContent}>
          <p>
            {format('we sent an email', {
              email: <strong key="email">{aaEmail}</strong>,
            })}
          </p>
          <p>{format('click the link in the email')}</p>
        </div>
        <div className={styles.section}>
          <PrimaryButton onClick={onDismiss}>{format('got it')}</PrimaryButton>
        </div>
      </AutoOnboardingDialog>
    );
  }
  return (
    <>
      <AlertContainer
        displayType="error"
        name={ATLASSIAN_PROFILE_SYNC_ALERT_NAME}
        area={ATLASSIAN_PROFILE_SYNC_ALERT_AREA}
      >
        {formatError('something-went-wrong')}
      </AlertContainer>
      <AutoOnboardingDialog
        title={format('new trello atlassian profile')}
        // Only show the help link for managed accounts
        helpLink={orgName && AtlassianAccountOnboardingLinks.Help}
        closeDialog={onDismiss}
        className={styles.atlassianProfileContent}
      >
        <div className={styles.confirmationContent}>
          <div className={styles.leftSection}>
            <MemberAvatarUnconnected size={64} avatars={avatars} />
            <p>{name}</p>
            <span title={aaEmail}>{aaEmail}</span>
          </div>
          <div className={styles.rightSection}>
            <li key={aaEmail}>
              {showMultiEmailMsg
                ? format('atlassian profile first li multi email', {
                    aaEmail: <strong key="multi-email">{aaEmail}</strong>,
                  })
                : format('atlassian profile first li', {
                    aaEmail: <strong key="email">{aaEmail}</strong>,
                  })}
            </li>
            <li key="atlassian profile second li">
              {format('atlassian profile second li')}
            </li>
            <li key="atlassian profile third li">
              {format('atlassian profile third li', { orgName })}
            </li>
            <li key="atlassian profile forth li">
              {format('atlassian profile forth li')}
            </li>
          </div>
        </div>
        <div className={styles.section}>
          <p>{format('finish account setup')}</p>
          <PrimaryButton
            size={ButtonSizes.DEFAULT}
            onClick={atlassianLogin}
            disabled={isLoading}
          >
            {format('continue to atlassian')}
          </PrimaryButton>
        </div>
      </AutoOnboardingDialog>
    </>
  );
};
